import { createEffect, createSignal, Match, Show, Switch } from "solid-js";
import { Spacer } from "../../widgets/spacer";
import { ButtonRegular } from "../../widgets/button";
import { NumberKeyboard } from "../voucher_edit/number_keyboard";
import { toDisplayNumber, toIndianNumber, toRupees } from "../../utils/number";
import { defaultCoin } from "../../assets/assets";
import { useClassicContext } from "~/data/classic_context";
import { pushPage, goBack } from "../../shared_states/modal";
import { HubbleRoute } from "../../shared_states/modal";

interface CustomCoinInputModalProps {
  maxCoins: number;
  currentCoins?: number;
  onApply: (coins: number) => void;
  coinIconUrl?: string;
  coinToInrConversionRatio: number;
  giftCardValue: number;
}

export function CustomCoinInputModal(props: CustomCoinInputModalProps) {
  const { data } = useClassicContext();
  const [coinAmount, setCoinAmount] = createSignal<number>(
    props.currentCoins ?? 0
  );
  const [error, setError] = createSignal<string | undefined>(undefined);

  // Helper to get balance
  const balance = () =>
    data.isLoggedIn ? data.v4HomePage.coins.totalAvailable : 0;

  const handleChange = (val: number | null) => {
    const num = val ?? 0;
    setCoinAmount(num);
    if (num > props.maxCoins) {
      setError(`Cannot use more than ${toIndianNumber(props.maxCoins)} coins.`);
    } else if (num < 1) {
      setError("Please enter a valid number of coins");
    } else {
      setError(undefined);
    }
  };

  const handleApply = () => {
    const val = coinAmount();
    if (val > props.maxCoins) {
      setError(`Cannot use more than ${toIndianNumber(props.maxCoins)} coins.`);
      return;
    }
    if (val < 1) {
      setError("Please enter a valid number of coins");
      return;
    }
    setError(undefined);
    props.onApply(val);
  };

  // Modal for "see why"
  function openSeeWhyModal() {
    pushPage(
      () => (
        <SeeWhyModal
          balance={balance()}
          maxCoins={props.maxCoins}
          giftCardValue={props.giftCardValue}
          coinToInrConversionRatio={props.coinToInrConversionRatio}
          coinIconUrl={props.coinIconUrl}
        />
      ),
      { isModal: true },
      HubbleRoute.SEE_WHY_COINS_LIMIT_MODAL
    );
  }

  return (
    <div class="flex h-full w-full flex-col items-center bg-white p-5">
      <Spacer height={20} />
      <div class="pb-2 text-mediumBold text-textDark">Enter coins to use</div>

      <div
        class={`h-[48px] w-full rounded-xl border bg-white px-[16px] py-[13px] transition-colors ${
          error() ? "!border-accentError" : "border-baseTertiaryDark"
        }`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleApply();
          }}
        >
          <input
            type="number"
            class="text-2xl w-full bg-transparent text-center font-bold outline-none"
            value={coinAmount()}
            placeholder="0"
            onInput={(e) => handleChange(parseInt(e.currentTarget.value) || 0)}
          />
        </form>
      </div>
      <div class="mt-[6px]"></div>
      <div class="flex w-full flex-row items-center justify-center  text-normal text-textNormal">
        <Switch>
          <Match when={error() && coinAmount() < 1}>
            <Spacer height={4} />
            <span class=" text-errorDark">{error()}</span>
          </Match>
          <Match when={true}>
            <Spacer height={4} />
            <span
              class="text-medium text-textNormal"
              classList={{
                "!text-errorDark": !!error(),
              }}
            >
              Balance: {toIndianNumber(balance())}
              <Show when={props.maxCoins < balance()}>
                {" "}
                • Max you can use: {toIndianNumber(props.maxCoins)}{" "}
                <span
                  class="cursor-pointer select-none font-semibold text-basePrimaryDark underline underline-offset-2"
                  onClick={openSeeWhyModal}
                >
                  See why
                </span>
              </Show>
            </span>
          </Match>
        </Switch>
      </div>
      <div class="mt-[16px]"></div>

      <div class=" w-full px-[6px] pb-[20px]">
        <NumberKeyboard
          class="!my-0"
          value={coinAmount()}
          onChange={(val) => handleChange(val)}
        />
      </div>
      <span
        class="pb-4 text-mediumBold  text-accentSuccessDark  transition-opacity duration-300"
        classList={{
          "opacity-[0]": !!error(),
        }}
      >
        {`Get ${toRupees(coinAmount() * props.coinToInrConversionRatio)} off`}
      </span>
      <ButtonRegular
        onClick={handleApply}
        isEnabled={coinAmount() >= 0 && coinAmount() <= props.maxCoins}
        class="button-filled w-full"
      >
        Use Coins
      </ButtonRegular>
    </div>
  );
}

// SeeWhyModal component
function SeeWhyModal(props: {
  balance: number;
  maxCoins: number;
  giftCardValue: number;
  coinToInrConversionRatio: number;
  coinIconUrl?: string;
}) {
  return (
    <div class="flex h-full w-full flex-col items-center justify-center bg-white p-6 font-semibold">
      <div class="text-lg mb-4 text-center text-[20px]  text-textDark">
        {`Max you can use to pay ${toDisplayNumber(((props.maxCoins * props.coinToInrConversionRatio) / props.giftCardValue) * 100)}% of total gift card value as coins`}
      </div>
      <div class="text-base mb-2 text-center font-medium text-textDark">
        You have:{" "}
        <img
          src={props.coinIconUrl || defaultCoin}
          class="mx-1 inline-block h-5 w-5 align-text-bottom"
        />
        {toIndianNumber(props.balance)}
      </div>
      <div class="my-4 w-full border-t border-baseTertiaryMedium"></div>
      <div class="flex w-full flex-col items-center gap-1 text-[15px] text-textDark">
        <div class=" flex w-full justify-between ">
          <span>Gift card value</span>
          <span class="text-textDark">{toRupees(props.giftCardValue)}</span>
        </div>
        <div class="my-4 w-full border-t border-baseTertiaryMedium"></div>

        <div class=" flex w-full justify-between">
          <span>Max you can use</span>
          <span class="text-textDark">
            <img
              src={props.coinIconUrl || defaultCoin}
              class="mx-1 inline-block h-5 w-5 align-text-bottom"
            />
            {toIndianNumber(props.maxCoins)}
          </span>
        </div>
      </div>
      <ButtonRegular
        isEnabled={true}
        class="mt-5 w-full"
        onClick={() => goBack()}
      >
        Got it
      </ButtonRegular>
    </div>
  );
}
